import { Link } from "react-router-dom";
import { Badge, buttonClass, Callout, Card, StatCard } from "@promptgatewaysadmin/ui";
import { useSession } from "../auth/SessionProvider.js";
import { useSubscriptionState, useUsage } from "../queries/hooks.js";
import { capabilities } from "../api/capabilities.js";
import { daysLeft, hasGuard, seatState, type SubscriptionState } from "../api/normalise.js";
import { Skeleton, ErrorState, OfflineBanner } from "../components/states.js";
import { Stat } from "../components/format.js";
import { CHROME_WEB_STORE_URL, config } from "../config.js";
import { getPlan } from "../pricing.js";

export function Dashboard() {
  const session = useSession();
  const sub = useSubscriptionState();
  const isAdmin = session.role === "admin";
  const first = (session.me.displayName || session.me.email).split(/[\s@]/)[0];

  return (
    <div className="pg-page">
      <OfflineBanner />

      <p className="pg-greeting">Welcome back, {first}.</p>

      {sub.isError ? (
        // Never guess a status. No pill, no hero — just an honest failure at the smallest
        // scope, with the rest of the page intact.
        <ErrorState error={sub.error} fallbackKey="error.billing.load" onRetry={() => void sub.refetch()} />
      ) : sub.isPending ? (
        <Skeleton height={120} />
      ) : (
        <Hero state={sub.data} isAdmin={isAdmin} />
      )}

      <UsageCards state={sub.data} />

      {sub.data && <SetupChecklist state={sub.data} />}
    </div>
  );
}

/**
 * The status-driven hero.
 *
 * daysLeft drives the copy; status drives what is reachable. Day 11 is a "gentle heads-up",
 * not a countdown — the tone shift is --warn on the pill plus ONE extra sentence. No red, no
 * timer, no modal, no interstitial: the trial was sold as "a calm reminder".
 */
function Hero({ state, isAdmin }: { state: SubscriptionState; isAdmin: boolean }) {
  const days = daysLeft(state.trialEndsAt);

  if (state.status === "locked") {
    // Copy may never imply a debt, a grace period that will charge, or lost data.
    return (
      <Card title="Your trial has ended">
        <p className="pg-card__body">
          Your team&apos;s access is paused. Nothing was charged, and nothing was stored — pick
          a plan and everything picks up where it left off.
        </p>
        {isAdmin && (
          <Link className={buttonClass("primary")} to="/checkout">
            Choose a plan
          </Link>
        )}
      </Card>
    );
  }

  if (state.status === "past_due") {
    return (
      <Callout tone="warn" title="Your last payment didn't go through" role="status">
        We&apos;ll retry automatically.{" "}
        {isAdmin ? (
          <>
            Update your card to avoid interruption. <Link to="/billing">Update card ↗</Link>
          </>
        ) : (
          "Ask your team admin to update the payment method."
        )}
      </Callout>
    );
  }

  if (state.status === "canceled") {
    return (
      <Card title="Your plan has ended">
        <p className="pg-card__body">Your invoices are still available in Billing.</p>
        {isAdmin && (
          <Link className={buttonClass("primary")} to="/checkout">
            Choose a plan
          </Link>
        )}
      </Card>
    );
  }

  if (state.status === "incomplete") {
    return (
      <Card title="Activating your plan">
        <p className="pg-card__body">This usually takes a few seconds.</p>
      </Card>
    );
  }

  if (state.status === "trialing") {
    const overLimit = seatState(state) === "over";
    return (
      <>
        {overLimit && <OverSeatLimit state={state} isAdmin={isAdmin} />}
        <Card
          title={
            days === null
              ? "Your trial is running"
              : days >= 14
                ? "Your 14-day trial has started"
                : days <= 0
                  ? "Last day of your trial"
                  : `${days} of 14 days left`
          }
        >
          {days !== null && days <= 3 && (
            <p className="pg-card__body">
              Add a plan to keep going. Still no card on file — nothing is charged
              automatically.
            </p>
          )}
          <p className="pg-card__body">
            <Badge tone="neutral">No credit card</Badge> <Badge tone="neutral">No auto-charge</Badge>{" "}
            <Badge tone="neutral">Cancel in one click</Badge>{" "}
            <Badge tone="neutral">Nothing stored, ever</Badge>
          </p>
          {isAdmin && (
            <Link className={buttonClass("primary")} to="/checkout">
              Choose a plan
            </Link>
          )}
        </Card>
      </>
    );
  }

  // active — no hero, no pill. A healthy account does not need to be told it is healthy.
  return seatState(state) === "over" ? <OverSeatLimit state={state} isAdmin={isAdmin} /> : null;
}

/**
 * Over the seat limit is framed as an OUTAGE, not a nudge: seat_valid is independent of
 * status, so a fully paid `active` org can be seat-invalid — and when it is, the whole org
 * is entitlement-invalid, not just the extra member.
 */
function OverSeatLimit({ state, isAdmin }: { state: SubscriptionState; isAdmin: boolean }) {
  const over = state.seatCount - state.seatLimit;
  return (
    <Callout tone="danger" title="Your team is over its seat limit" role="status">
      {over} seat{over === 1 ? "" : "s"} over. Everyone&apos;s access is paused until you add
      seats or remove members.{" "}
      {isAdmin && <Link to="/team">Add seats ↗</Link>}
    </Callout>
  );
}

/**
 * Three stat cards.
 *
 * Card 3 is STATIC — "Data stored / 0 KB / By design" is a statement of architecture, not a
 * measurement, and it renders in every state including loading, error, offline and locked.
 * The contract backs that: stored_bytes is `enum: [0]` and storage_by_design is `enum:
 * [true]`, so a non-zero value would fail to parse rather than render, which is the correct
 * outcome — it would mean the platform's no-storage guarantee had been broken upstream.
 *
 * Cards 1-2 are HIDDEN, not zeroed, on locked and canceled: a paused account has not used
 * nothing, it has been paused.
 */
function UsageCards({ state }: { state: SubscriptionState | undefined }) {
  // The consent coupling. When the preference is off the portal does not call the endpoint
  // AT ALL — a consent that still sends the request is theatre.
  const metricsOn = usageMetricsEnabled(state);
  const hidden = state?.status === "locked" || state?.status === "canceled";
  const usage = useUsage({ enabled: metricsOn && !hidden && state !== undefined });

  const guard = state ? hasGuard(state) : false;

  // Until the subscription resolves we do not know whether cards 1-2 belong on this screen
  // at all — a locked org hides them. Rendering them first and removing them a beat later
  // would flash "Prompts enhanced" at a customer whose account is paused, which is exactly
  // the "you have used nothing" reading the hidden-not-zeroed rule exists to prevent.
  // Card 3 is unaffected: it is a statement of architecture and always renders.
  if (state === undefined) {
    return (
      <div className="pg-stats">
        <StatCard label="" value={<Skeleton width={120} height={56} />} />
        <StatCard label="" value={<Skeleton width={120} height={56} />} />
        <StorageCard />
      </div>
    );
  }

  if (!metricsOn) {
    return (
      <div className="pg-stats">
        <p className="pg-stats__consent">
          Usage counts are off. Turn on anonymous usage metrics in{" "}
          <Link to="/settings">Settings</Link> to see them.
        </p>
        <StorageCard />
      </div>
    );
  }

  if (hidden) {
    return (
      <div className="pg-stats">
        <StorageCard />
      </div>
    );
  }

  const data = usage.data?.usage;
  const loading = usage.isPending && !usage.isError;

  return (
    <div className="pg-stats">
      <StatCard
        label="Prompts enhanced"
        value={
          loading ? (
            <Skeleton width={64} height={32} />
          ) : (
            <Stat value={data?.prompts_this_week ?? null} />
          )
        }
        sublabel={sublabelFor(loading, data?.prompts_this_week, "This week", "No prompts yet this week")}
      />

      {/* Card 2 swaps IDENTITY on the Guard entitlement. Redaction is Guard-only and Guard
          is contact-sales, so for most orgs "Redactions applied" would read 0 forever.
          Content moderation runs on every plan, so the fallback card is truthful. */}
      {guard ? (
        <StatCard
          label="Redactions applied"
          value={
            loading ? <Skeleton width={64} height={32} /> : <Stat value={data?.redactions_applied ?? null} />
          }
          sublabel="Names, emails, IDs"
        />
      ) : (
        <StatCard
          label="Checks passed"
          // PortalUsage has no moderation-check field, confirmed against the merged
          // contract. So this renders the honest unknown rather than borrowing
          // redactions_applied — which would be 0 and wrong.
          value={<Stat value={null} />}
          sublabel={
            <>
              Content moderation ·{" "}
              <a href={`${config.siteOrigin}/contact-sales`}>Add Prompt Guard</a>
            </>
          }
        />
      )}

      <StorageCard />
    </div>
  );
}

function sublabelFor(
  loading: boolean,
  value: number | undefined,
  normal: string,
  emptyText: string,
): string {
  if (loading) return normal;
  if (value === undefined) return "Usage data unavailable";
  // A real 0 and an unknown are DIFFERENT renderings. Collapsing them tells a customer they
  // have used nothing.
  return value === 0 ? emptyText : normal;
}

function StorageCard() {
  return <StatCard label="Data stored" value="0 KB" sublabel="By design" />;
}

/**
 * Until Me.preferences merges there is no account-level record of this consent, so the
 * portal treats it as on. That is the specced degraded state: the four genuinely
 * device-local Settings rows are unaffected, and the three account-level ones say so.
 */
function usageMetricsEnabled(_state: SubscriptionState | undefined): boolean {
  if (!capabilities.preferences) return true;
  return true;
}

function SetupChecklist({ state }: { state: SubscriptionState }) {
  if (state.status === "locked" || state.status === "canceled") return null;
  const planName = getPlan(state.plan).displayName;
  return (
    <Card title="Get set up">
      <ul className="pg-checklist">
        <li>
          <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
            Install the browser extension
          </a>
        </li>
        {state.plan !== "individual" && (
          <>
            <li>
              <Link to="/team">Invite your team</Link>
            </li>
            <li>
              <Link to="/team/policy">Set your shared policy</Link>
            </li>
          </>
        )}
      </ul>
      <p className="pg-card__body">
        You&apos;re on <strong>{planName}</strong>.
      </p>
    </Card>
  );
}
